import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { usePrefersReducedMotion } from '@hooks';

const StyledActivitiesSection = styled.section`
  max-width: 700px;

  .timeline {
    ${({ theme }) => theme.mixins.resetList};
    position: relative;
    margin-top: 50px;
  }
`;

const StyledGroup = styled.li`
  position: relative;
  padding-left: 45px;
  padding-bottom: 50px;
  border-left: 2px solid var(--lightest-navy);

  &:last-of-type {
    padding-bottom: 0;
  }

  @media (max-width: 480px) {
    padding-left: 30px;
  }

  /* one node per month, aligned with the date */
  &:before {
    content: '';
    position: absolute;
    top: 4px;
    left: -9px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: var(--green);
    box-shadow: 0 0 0 4px var(--navy);
  }

  .activity-date {
    display: block;
    margin-bottom: 8px;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
  }

  .activity {
    /* Preserve the original content width, including both former borders. */
    padding-right: 52px;

    &:not(:first-of-type) {
      margin-top: 25px;
    }

    @media (max-width: 480px) {
      padding-right: 42px;
    }
  }

  .activity-title {
    margin: 0;
    color: var(--lightest-slate);
    font-size: clamp(20px, 4vw, var(--fz-heading));
    line-height: 1.2;

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  .activity-body {
    margin-top: 20px;
    color: var(--light-slate);
    font-size: var(--fz-lg);
    overflow-wrap: anywhere;

    > :first-child {
      margin-top: 0;
    }

    > :last-child {
      margin-bottom: 0;
    }

    p {
      margin: 0 0 1em;
    }

    figure {
      margin: 24px 0;
    }

    img,
    video,
    iframe {
      max-width: 100%;
    }

    img,
    video {
      height: auto;
    }

    .gatsby-resp-image-wrapper {
      border-radius: var(--border-radius);
      overflow: hidden;
    }

    .gatsby-resp-image-image {
      border-radius: var(--border-radius);
      opacity: 1 !important;
      transition: none !important;
    }

    figcaption {
      margin-top: 10px;
      color: var(--slate);
      font-size: var(--fz-sm);
      font-style: italic;
      line-height: 1.5;
      text-align: center;
    }

    .photo-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(min(180px, 100%), 1fr));
      gap: 20px;
    }

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }

    a.gatsby-resp-image-link {
      &::after {
        display: none;
      }

      &:focus-visible {
        outline: 2px solid var(--green);
        outline-offset: 4px;
      }
    }

    strong {
      color: var(--white);
      font-weight: 600;
    }
  }
`;

const Activities = () => {
  const data = useStaticQuery(graphql`
    query {
      activities: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/activities/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              date
              title
            }
            html
          }
        }
      }
    }
  `);

  const activities = data.activities.edges.filter(({ node }) => node);

  // Group consecutive activities that share the same month so they show one dot + date.
  const groups = [];
  activities.forEach(({ node }) => {
    const displayDate = new Date(node.frontmatter.date).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.date === displayDate) {
      lastGroup.items.push(node);
    } else {
      groups.push({ date: displayDate, items: [node] });
    }
  });

  const revealTitle = useRef(null);
  const revealGroups = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealGroups.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, [prefersReducedMotion]);

  return (
    <StyledActivitiesSection id="activities">
      <h2 className="numbered-heading" ref={revealTitle}>
        Recent Activities
      </h2>

      <ol className="timeline">
        {groups.map((group, i) => (
          <StyledGroup key={i} ref={el => (revealGroups.current[i] = el)}>
            <span className="activity-date">{group.date}</span>

            {group.items.map((node, j) => {
              const { frontmatter, html } = node;
              const { title } = frontmatter;

              return (
                <article className="activity" key={j}>
                  <h3 className="activity-title">{title}</h3>

                  <div className="activity-body" dangerouslySetInnerHTML={{ __html: html }} />
                </article>
              );
            })}
          </StyledGroup>
        ))}
      </ol>
    </StyledActivitiesSection>
  );
};

export default Activities;
